'use client';

import { useSearchParams } from 'next/navigation';
import { useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Pagination } from '@module/mdrt-org/shared/components/pagination/pagination';
import { type MembershipLevel } from '@module/mdrt-org/shared/utils/enums/membership';
import { getOptionByValue } from '@module/mdrt-org/shared/utils/helpers/get-option-by-value';
import { getQueryParametersStringFromObject } from '@module/mdrt-org/shared/utils/helpers/getQueryParametersStringFromObject';
import { type Designation } from '@module/mdrt-org/shared/utils/types/designation';
import { formBaseConfig } from '@shared/utils/constants/formBaseConfig';
import { GTM_ACTION_CLICK, GTM_EVENT_MEMBER_DIRECTORY_SEARCH, GTM_EVENT_USER_INTERACTIONS } from '@shared/utils/constants/gtm';
import { sanitizeUrlTextParameter } from '@shared/utils/helpers/sanitize-url-text-parameter';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';
import { useSafePagination } from '@shared/utils/hooks/use-safe-pagination';
import { type SelectOption } from '@shared/utils/types/select-option';
import { type SearchMembersRequest } from '../../utils/types';
import { type MemberDirectoryPageContent } from '../get-member-directory-page-content';
import { SearchForm } from '../search-form/search-form';
import { NoResultsCard } from '../search-results/no-results-card';
import { SearchResults } from '../search-results/search-results';
import { useCountryStatesQuery } from '../use-country-states-query';
import { useMemberDirectorySearchQuery } from '../use-member-directory-search-query';
import styles from './search.module.scss';
export type SearchFormData = {
  city: string;
  country: Nullable<SelectOption<number>>;
  membershipLevel: Nullable<SelectOption<string>>;
  searchQuery: string;
  stateOrProvince: Nullable<SelectOption<number>>;
};
const ITEMS_PER_PAGE = 24;
export const Search = ({
  canFilterByMembershipLevel,
  cmsContent,
  countryOptions,
  designations,
  membershipLevelOptions
}: {
  canFilterByMembershipLevel: boolean;
  cmsContent: MemberDirectoryPageContent;
  countryOptions: Array<SelectOption<number>>;
  designations: Designation[];
  membershipLevelOptions: Array<SelectOption<MembershipLevel>>;
}) => {
  const searchParameters = useSearchParams();
  const resultsListHeaderRef = useRef<HTMLHeadingElement>(null);
  const getParameter = <T extends keyof SearchMembersRequest,>(key: T,
  // eslint-disable-next-line @typescript-eslint/no-shadow
  transform: (value: string) => SearchMembersRequest[T] = value => value as SearchMembersRequest[T]) => {
    const value = searchParameters.get(key);
    return value ? {
      [key]: transform(value)
    } : {};
  };
  const searchParametersString = searchParameters.toString();
  const requestData: SearchMembersRequest = useMemo(() => ({
    ...getParameter('city', sanitizeUrlTextParameter),
    ...getParameter('countryId', Number),
    ...(canFilterByMembershipLevel && getParameter('membershipLevel')),
    ...getParameter('page', value => Number(value) > 0 ? Number(value) : 1),
    ...getParameter('searchQuery', sanitizeUrlTextParameter),
    ...getParameter('stateOrProvinceId', Number)
  }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [searchParametersString]);
  const {
    stateOptions: countryStatesOptions
  } = useCountryStatesQuery({
    selectedCountryId: requestData?.countryId
  });
  const defaultFormValues = {
    city: requestData.city ?? '',
    country: getOptionByValue(countryOptions, requestData?.countryId),
    membershipLevel: getOptionByValue(membershipLevelOptions, requestData?.membershipLevel),
    searchQuery: requestData.searchQuery ?? '',
    stateOrProvince: getOptionByValue(countryStatesOptions, requestData.stateOrProvinceId)
  };
  const formMethods = useForm<SearchFormData>({
    ...formBaseConfig,
    defaultValues: defaultFormValues
  });
  const handleSearchRequestDataUpdate = (newRequestData: SearchMembersRequest) => {
    const urlWithSearchParameters = getQueryParametersStringFromObject({
      ...requestData,
      ...newRequestData
    });
    window.history.pushState(null, '', `?${urlWithSearchParameters}`);

    // Timeouts are added to fix iOS issue.
    setTimeout(() => {
      resultsListHeaderRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }, 0);
  };
  const handleSubmit = async (formData: SearchFormData) => {
    handleSearchRequestDataUpdate({
      city: sanitizeUrlTextParameter(formData.city) || undefined,
      countryId: formData.country?.value ?? undefined,
      membershipLevel: formData.membershipLevel?.value ?? undefined,
      page: 1,
      searchQuery: sanitizeUrlTextParameter(formData.searchQuery) || undefined,
      stateOrProvinceId: formData.stateOrProvince?.value ?? undefined
    });
    sendGtmEvent({
      action: GTM_ACTION_CLICK,
      event: GTM_EVENT_USER_INTERACTIONS,
      eventName: GTM_EVENT_MEMBER_DIRECTORY_SEARCH,
      searchResultsValues: {
        q_selectedTerms: {
          city: formData.city.trim() || undefined,
          country: formData.country?.label ?? undefined,
          membershipLevel: formData.membershipLevel?.label ?? undefined,
          state: formData.stateOrProvince?.label ?? undefined
        },
        search_term: formData.searchQuery
      }
    });
  };
  const setPage = (page: number) => {
    handleSearchRequestDataUpdate({
      page
    });
  };
  const {
    data: searchResults,
    isSearchResultsDataLoading
  } = useMemberDirectorySearchQuery({
    requestData: {
      ...requestData
    }
  });
  const currentPage = requestData.page ? Number(requestData.page) : 1;
  useSafePagination({
    currentPageItemsCount: searchResults?.users.length,
    isFetching: isSearchResultsDataLoading,
    page: currentPage,
    pageSize: ITEMS_PER_PAGE,
    setPage,
    totalItemsCount: searchResults?.count
  });
  return <div className={styles['sections-container']}>
      <div className={styles['section']}>
        <div className={styles['section__primary-content']}>
          <h1 className={styles['section__title']}>{cmsContent.pageTitle}</h1>
          <SearchForm canFilterByMembershipLevel={canFilterByMembershipLevel} cmsContent={cmsContent} countryOptions={countryOptions} formMethods={formMethods} membershipLevelOptions={membershipLevelOptions} onSubmit={handleSubmit} />
        </div>
        {/* todo Members Near By block <div className={styles['section__secondary-content']} />*/}
      </div>
      {searchResults && (searchResults.count > 0 && searchResults.users.length > 0 ? <div className={styles['results-section']}>
            <SearchResults cmsContent={cmsContent} designations={designations} resultsListHeaderRef={resultsListHeaderRef} searchResults={searchResults} />
            <div className={styles['pagination-container']}>
              <Pagination blurButtonsOnPageChange currentPage={currentPage} label={{
          nextPage: cmsContent.nextLinkLabel,
          pagination: cmsContent.paginationLabel,
          previousPage: cmsContent.previousPageLabel,
          totalItemsCount: cmsContent.totalItemsCountLabel
        }} maxItemsPerPage={ITEMS_PER_PAGE} onPageChange={setPage} totalItemsCount={searchResults?.count} />
            </div>
          </div> : (searchResults?.count === 0 || searchResults.users.length === 0) && !isSearchResultsDataLoading && <NoResultsCard cmsContent={cmsContent} />)}
    </div>;
};